import { storyblokEditable } from "@storyblok/react/rsc";
import { ArrowRight } from "lucide-react";
import Link from "next/link";
import { getUrlFromMultilink } from "@/lib/utils";
import type {
  Links as LinksBlok,
  NavigationLink,
  StoryblokStoryData,
} from "@/types/storyblok";
import { fetchPaginatedPages } from "./actions";
import { PaginatedLinks, type ProcessedLink } from "./Pagination";

export default async function Links({ blok }: { blok: LinksBlok }) {
  const subtitleLinkUrl = blok.subtitle_link
    ? getUrlFromMultilink(blok.subtitle_link)
    : null;
  const hasValidSubtitleLink =
    subtitleLinkUrl && subtitleLinkUrl !== "#" && subtitleLinkUrl.trim() !== "";

  // Fetch first page of relational pages if link_type is "relational"
  const itemsPerPage = 7;
  const initialRelationalData =
    blok.link_type === "relational" && blok.relations
      ? await fetchPaginatedPages(blok.relations, 1, itemsPerPage)
      : null;

  return (
    <div
      className="relative"
      // biome-ignore lint/suspicious/noExplicitAny: Required for Storyblok React compatibility
      {...storyblokEditable(blok as any)}
    >
      <div className="max-w-screen-2xl mx-auto px-4 sm:px-6 relative isolate pt-24 pb-48">
        <div className="flex flex-col space-y-4 max-w-screen-lg">
          {blok.subtitle &&
            (hasValidSubtitleLink ? (
              <Link
                href={subtitleLinkUrl}
                className="text-teal-500 flex items-center gap-2 text-2xl drop-shadow-2xl font-medium font-display uppercase hover:text-teal-600 transition-colors duration-300"
              >
                {blok.subtitle} <ArrowRight />
              </Link>
            ) : (
              <span className="text-teal-500 text-2xl drop-shadow-2xl font-medium font-display uppercase">
                {blok.subtitle}
              </span>
            ))}
          {blok.title && (
            <h2 className="text-5xl md:text-6xl drop-shadow-2xl font-extrabold font-display bg-gradient-to-br from-violet-100 via-zinc-50 to-violet-200 bg-clip-text text-transparent leading-tight pb-1">
              {blok.title}
            </h2>
          )}
          {blok.lead && (
            <p className="text-xl drop-shadow-2xl max-w-prose font-medium text-muted-foreground mt-4 sm:mt-6 mb-8">
              {blok.lead}
            </p>
          )}
        </div>
        {blok.link_type === "manual" &&
          blok.links &&
          blok.links.length > 0 &&
          (blok.links.length > 2 ? (
            <PaginatedLinks
              links={blok.links.map(
                (navLink: NavigationLink): ProcessedLink => ({
                  url: getUrlFromMultilink(navLink.link),
                  title: navLink.link?.title || "Link",
                  key: navLink._uid,
                })
              )}
            />
          ) : (
            <div className="flex flex-col divide-y-4 border-y-4 border-violet-900 divide-violet-900 mt-12">
              {blok.links.map((navLink: NavigationLink) => {
                const linkUrl = getUrlFromMultilink(navLink.link);
                const linkTitle = navLink.link?.title || "Link";

                return (
                  <div
                    key={navLink._uid}
                    className="group relative py-8 hover:px-8 transition-all duration-300 overflow-hidden"
                  >
                    <div className="absolute inset-0 bg-gradient-to-br from-violet-900 via-violet-900 to-violet-950 scale-x-0 group-hover:scale-x-100 origin-left transition-transform duration-300" />
                    <Link
                      href={linkUrl}
                      className="relative text-4xl md:text-6xl font-display drop-shadow-2xl group-hover:text-teal-500 transition-all duration-300 flex items-center justify-between gap-12 z-10"
                    >
                      <span className="flex items-center gap-8">
                        {linkTitle}
                      </span>
                      <ArrowRight className="size-10 shrink-0 md:size-24" />
                    </Link>
                  </div>
                );
              })}
            </div>
          ))}
        {blok.link_type === "relational" &&
          initialRelationalData &&
          initialRelationalData.stories.length > 0 &&
          (initialRelationalData.total >= itemsPerPage ? (
            <PaginatedLinks
              initialLinks={initialRelationalData.stories.map(
                (story: StoryblokStoryData): ProcessedLink => ({
                  url: `/${story.full_slug}`,
                  title:
                    (story.content &&
                    "title" in story.content &&
                    typeof story.content.title === "string"
                      ? story.content.title
                      : null) ||
                    story.name ||
                    "Link",
                  key: story.uuid,
                })
              )}
              total={initialRelationalData.total}
              route={blok.relations || undefined}
              itemsPerPage={itemsPerPage}
            />
          ) : (
            <div className="flex flex-col divide-y-4 border-y-4 border-violet-900 divide-violet-900 mt-12">
              {initialRelationalData.stories.map(
                (story: StoryblokStoryData) => {
                  const linkUrl = `/${story.full_slug}`;
                  const linkTitle =
                    (story.content &&
                    "title" in story.content &&
                    typeof story.content.title === "string"
                      ? story.content.title
                      : null) ||
                    story.name ||
                    "Link";

                  return (
                    <div
                      key={story.uuid}
                      className="group relative py-8 hover:px-8 transition-all duration-300 overflow-hidden"
                    >
                      <div className="absolute inset-0 bg-gradient-to-br from-violet-900 via-violet-900 to-violet-950 scale-x-0 group-hover:scale-x-100 origin-left transition-transform duration-300" />
                      <Link
                        href={linkUrl}
                        className="relative text-4xl md:text-6xl font-display drop-shadow-2xl group-hover:text-teal-500 transition-all duration-300 flex items-center justify-between gap-12 z-10"
                      >
                        <span className="flex items-center gap-8">
                          {linkTitle}
                        </span>
                        <ArrowRight className="size-10 shrink-0 md:size-24" />
                      </Link>
                    </div>
                  );
                }
              )}
            </div>
          ))}
      </div>
    </div>
  );
}
